import { Draft } from 'immer';
import * as _ from 'lodash';

import {
  createBorrowerShell,
  createCapitalProviderShell,
  createServicerShell,
  createVerificationAgentShell,
} from '@finley-technologies/credit-agreement-utils';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import { BankAccount as FormBankAccount, BaseFormFields } from '../types';
import { createBankAccountEntityShell } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditBankAccountModal';
import { Organization as FormOrg } from '~/core/routes/settings/implementations/common/types';

type CreditAgreementFullArtifact = CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

const formOrgToCaParty = <
  T extends CreditAgreement.DomainEntity.Items.BaseOrganizationEntity
>(
  formOrg: FormOrg,
  existingParties: readonly T[],
  createPartyShell: (orgId: string) => T,
): T => {
  // There are fields on the CA party entity that implementation portal is not yet aware of.
  // We need to ensure these are preserved.
  const existingParty = existingParties.find((p) => p.entity.id === formOrg.id);

  const newParty = existingParty
    ? _.cloneDeep(existingParty)
    : createPartyShell(formOrg.id);

  newParty.entity.id = formOrg.id;
  newParty.entity.slug = formOrg.slug;
  newParty.entity.name = formOrg.name;

  return newParty;
};

const formBankAccountToCaBankAccount = (
  formAccount: FormBankAccount,
  existingAccounts: readonly CreditAgreement.DomainEntity.Items.BankAccountEntity[],
): CreditAgreement.DomainEntity.Items.BankAccountEntity => {
  // There are fields on the CA bank account type that implementation portal is not yet aware of.
  // We need to ensure these are preserved.
  const existingAccount = existingAccounts.find(
    (a) => a.entity.id === formAccount.id && a.type.value === formAccount.type,
  );

  const newAccount = existingAccount
    ? _.cloneDeep(existingAccount)
    : createBankAccountEntityShell();

  newAccount.entity.id = formAccount.id;
  newAccount.type.value = Bank.AccountTypeSchema.parse(formAccount.type);
  newAccount.entity.name = formAccount.name;

  return newAccount;
};

const toAccountContainer = (
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[],
  name: 'Source Accounts' | 'Destination Accounts',
): CreditAgreement.DomainEntity.LinkedAccountPropertyContainer => ({
  name,
  type:
    CreditAgreement.DomainEntity.PropertyContainerType
      .LinkedAccountPropertyContainer,
  contents: bankAccounts.map((bankAccount) => ({
    type: CreditAgreement.DomainEntity.PropertyType.LinkedAccountProperty,
    value: bankAccount.entity.id,
    name: bankAccount.entity.name,
    _isWrittenIn_: true,
  })),
  _isWrittenIn_: true,
});

/**
 * Updates the source/destination accounts of funding request components to
 * match the current parties.
 */
const updateFundingRequestMechanics = (
  draft: Draft<CreditAgreementFullArtifact>,
) => {
  const borrowers = draft.facilityMechanics.parties.borrowers;
  const capitalProviders = draft.facilityMechanics.parties.capitalProviders;

  const parentOperatingAccounts = borrowers
    .flatMap((b) => b.bankAccounts)
    .filter(({ type }) => type.value === 'parentOperatingAccount');
  const collectionsAccounts = borrowers
    .flatMap((b) => b.bankAccounts)
    .filter(({ type }) => type.value === 'collections');

  const capitalProviderFundingAccounts = capitalProviders
    .flatMap((b) => b.bankAccounts)
    .filter(({ type }) => type.value === 'capitalProviderFundingAccount');

  const frm = draft.fundingRequestMechanics;

  // Collections paydown: collections account => capital provider.
  frm.collectionsPaydown.sourceAccounts = toAccountContainer(
    collectionsAccounts,
    'Source Accounts',
  );
  frm.collectionsPaydown.destinationAccounts = toAccountContainer(
    capitalProviderFundingAccounts,
    'Destination Accounts',
  );

  // Collections recycle: collections account => parent operating account.
  frm.collectionsRecycle.sourceAccounts = toAccountContainer(
    collectionsAccounts,
    'Source Accounts',
  );
  frm.collectionsRecycle.destinationAccounts = toAccountContainer(
    parentOperatingAccounts,
    'Destination Accounts',
  );

  // Cure paydown: parent operating account => capital provider.
  frm.curePaydown.sourceAccounts = toAccountContainer(
    parentOperatingAccounts,
    'Source Accounts',
  );
  frm.curePaydown.destinationAccounts = toAccountContainer(
    capitalProviderFundingAccounts,
    'Destination Accounts',
  );

  // Draw request: capital provider => parent operating account.
  frm.drawRequest.sourceAccounts = toAccountContainer(
    capitalProviderFundingAccounts,
    'Source Accounts',
  );
  frm.drawRequest.destinationAccounts = toAccountContainer(
    parentOperatingAccounts,
    'Destination Accounts',
  );
};

// This function receives a 'draft' of the Credit Agreement, which is provided by the immer library.
// This means that it is ok to mutate the `draft` variable - this produces a new CA object, and leaves
// the original CA reference untouched.
export const setFormValues = (
  draft: Draft<CreditAgreementFullArtifact>,
  values: BaseFormFields,
) => {
  const draftParties = draft.facilityMechanics.parties;

  draftParties.borrowers = values.borrowers.map((borrower) => {
    const newBorrower = formOrgToCaParty(
      borrower,
      draftParties.borrowers,
      createBorrowerShell,
    );
    newBorrower.borrowerEntityName.description = borrower.description;
    newBorrower.bankAccounts = borrower.bankAccounts.map((ba) =>
      formBankAccountToCaBankAccount(ba, newBorrower.bankAccounts),
    );
    return newBorrower;
  });

  draftParties.capitalProviders = values.capitalProviders.map(
    (capitalProvider) => {
      const newCapitalProvider = formOrgToCaParty(
        capitalProvider,
        draftParties.capitalProviders,
        createCapitalProviderShell,
      );
      newCapitalProvider.bankAccounts = capitalProvider.bankAccounts.map((ba) =>
        formBankAccountToCaBankAccount(ba, newCapitalProvider.bankAccounts),
      );
      return newCapitalProvider;
    },
  );

  draftParties.verificationAgents = values.verificationAgents.map(
    (verificationAgent) => {
      // No additional fields to set for verification agent.
      return formOrgToCaParty(
        verificationAgent,
        draftParties.verificationAgents,
        createVerificationAgentShell,
      );
    },
  );

  draftParties.servicers = values.servicers.map((servicer) => {
    // No additional fields to set for servicer.
    return formOrgToCaParty(
      servicer,
      draftParties.servicers,
      createServicerShell,
    );
  });

  updateFundingRequestMechanics(draft);
};
